import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getBackendUrl } from '@/lib/backendUrl';
import { ArrowLeft, Receipt, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { useSalesInvoicingModuleStatus } from '../hooks/useSalesInvoicingModuleStatus';
import { SalesInvoicingPausedNotice } from '../components/sales-invoicing/SalesInvoicingPausedNotice';
import ReferenceListsPanel from '../components/sales-invoicing/ReferenceListsPanel';
import MappingWorkbenchPanel from '../components/sales-invoicing/MappingWorkbenchPanel';
import DecisionQueuePanel from '../components/sales-invoicing/DecisionQueuePanel';
import CompletedJobsPanel from '../components/sales-invoicing/CompletedJobsPanel';
import ExportPanel from '../components/sales-invoicing/ExportPanel';
import LinkJobsPanel from '../components/sales-invoicing/LinkJobsPanel';
import SettingsPanel from '../components/sales-invoicing/SettingsPanel';

const API = getBackendUrl();

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function SalesInvoicingPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [isProduction, setIsProduction] = useState(false);
  const { enabled, loading: moduleStatusLoading, pausedMessage } = useSalesInvoicingModuleStatus();

  useEffect(() => {
    if (!enabled) return;
    axios.get(`${API}/api/sales-invoicing/environment`, { headers: authHeaders() })
      .then(({ data }) => setIsProduction(!!data.is_production))
      .catch(() => {});
  }, [enabled]);

  if (moduleStatusLoading) {
    return <div className="max-w-7xl mx-auto px-4 py-6" data-testid="sales-invoicing-page" />;
  }

  if (!enabled) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="sales-invoicing-page">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/home')} data-testid="sales-invoicing-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-semibold">Sales Invoicing</h1>
        </div>
        <SalesInvoicingPausedNotice pausedMessage={pausedMessage} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="sales-invoicing-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/home')} data-testid="sales-invoicing-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Receipt className="h-6 w-6 text-rose-600 flex-shrink-0" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Sales Invoicing</h1>
              <p className="text-sm text-slate-500" data-testid="sales-invoicing-subtitle">
                Reference lists, mapping, invoice drafts &amp; Spreadsheet Sync staging export{!isProduction && ' — Preview only'}
              </p>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/reports/invoice-list')}
          data-testid="sales-invoicing-goto-invoice-list-btn"
          className="self-start sm:self-auto"
        >
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Invoice List
        </Button>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm px-4 py-3 mb-6" data-testid="sales-invoicing-scope-notice">
        The Spreadsheet Sync export above is a staging file only — it never posts to or syncs with QBO. Separately, a Preview-only
        direct connection to a QuickBooks <strong>Sandbox</strong> company exists (Settings → QBO Sandbox connection) for reviewed,
        one-at-a-time invoice creation; Production QuickBooks stays blocked on the server regardless of connection state.
      </div>

      <Tabs defaultValue="completed-jobs" className="w-full">
        <TabsList data-testid="sales-invoicing-tabs" className="w-full overflow-x-auto justify-start">
          <TabsTrigger value="completed-jobs" data-testid="sales-invoicing-tab-completed-jobs">Completed Jobs</TabsTrigger>
          <TabsTrigger value="decision-queue" data-testid="sales-invoicing-tab-decision-queue">Decision Queue</TabsTrigger>
          <TabsTrigger value="link-jobs" data-testid="sales-invoicing-tab-link-jobs">Link Jobs</TabsTrigger>
          <TabsTrigger value="export" data-testid="sales-invoicing-tab-export">Export</TabsTrigger>
          <TabsTrigger value="workbench" data-testid="sales-invoicing-tab-workbench">Mapping Workbench</TabsTrigger>
          <TabsTrigger value="reference-lists" data-testid="sales-invoicing-tab-reference-lists">Reference Lists</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings" data-testid="sales-invoicing-tab-settings">Settings</TabsTrigger>}
        </TabsList>
        <TabsContent value="completed-jobs" className="mt-4">
          <CompletedJobsPanel />
        </TabsContent>
        <TabsContent value="decision-queue" className="mt-4">
          <DecisionQueuePanel />
        </TabsContent>
        <TabsContent value="link-jobs" className="mt-4">
          <LinkJobsPanel />
        </TabsContent>
        <TabsContent value="export" className="mt-4">
          <ExportPanel />
        </TabsContent>
        <TabsContent value="workbench" className="mt-4">
          <MappingWorkbenchPanel />
        </TabsContent>
        <TabsContent value="reference-lists" className="mt-4">
          <ReferenceListsPanel />
        </TabsContent>
        {isAdmin && (
          <TabsContent value="settings" className="mt-4">
            <SettingsPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
